import { useEffect, useState, lazy, Suspense, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { BookOpen, MessageSquare, History, Loader2 } from "lucide-react";

const SyncHistoryView = lazy(() =>
  import("./components/SyncHistoryView.js").then((m) => ({ default: m.SyncHistoryView })),
);
import { api, type Page, type SectionInfo } from "./lib/api.js";
import { cn } from "./lib/cn.js";
import { getUserRole } from "./lib/auth.js";
import {
  Card,
  CardContent,
} from "./components/ui/card.js";
import { CorpusBrowser } from "./components/CorpusBrowser.js";
import { ChatPanel } from "./components/ChatPanel.js";
import { SignOutButton, UnconfirmedPanel } from "./components/LoginPanel.js";
import { AuthShell } from "./components/AuthShell.js";
import { WsProvider } from "./lib/ws-client.js";
import { UserManagementModal } from "./components/UserManagementModal.js";
import { SearchBar } from "./components/SearchBar.js";
import { ActivityPanel } from "./components/ActivityPanel.js";
import { PageDetailPanel } from "./components/PageDetailPanel.js";
import { SessionPicker } from "./components/SessionPicker.js";
import { useSyncActivity, type JobActivity } from "./lib/syncEvents.js";

type SelectedPage = Page & {
  siteId: string;
  notebookId: string;
  notebookName: string;
  sectionId: string;
  sectionName: string;
};

const SESSION_LS_KEY = "dp-onenote.currentSessionId";
type Tab = "corpus" | "chat" | "history";

export default function App() {
  const [, setAuthReady] = useState(false);
  const [selectedPage, setSelectedPage] = useState<SelectedPage | null>(null);
  const [activeTab, setActiveTab] = useState<Tab>("chat");
  const [draftSection, setDraftSection] = useState<SectionInfo | null>(null);
  const [showUserMgmt, setShowUserMgmt] = useState(false);

  // Auth is now handled fully in-app via AuthShell + cognitoClient (no
  // Hosted UI redirect). The SSE stream now uses a fetch-based client
  // that sends the Authorization header directly, so no cookie priming
  // is needed.
  useEffect(() => {
    setAuthReady(true);
  }, []);

  const [currentSessionId, setCurrentSessionId] = useState<string | null>(() => {
    try {
      return localStorage.getItem(SESSION_LS_KEY);
    } catch {
      return null;
    }
  });

  useEffect(() => {
    try {
      if (currentSessionId) localStorage.setItem(SESSION_LS_KEY, currentSessionId);
      else localStorage.removeItem(SESSION_LS_KEY);
    } catch {
      /* ignore */
    }
  }, [currentSessionId]);

  // NOTE: useSyncActivity must run INSIDE <WsProvider>. We invoke it via the
  // <WithActivity> render-prop component below, not at App's top level —
  // when called here, useContext(WsContext) returns the default no-op
  // subscribe and WS frames silently never reach the activity reducer.

  const sessionQ = useQuery({
    queryKey: ["session", currentSessionId],
    queryFn: () => api.getSession(currentSessionId!),
    enabled: !!currentSessionId,
  });

  useEffect(() => {
    if (sessionQ.isError && sessionQ.error instanceof Error && sessionQ.error.message.startsWith("404")) {
      setCurrentSessionId(null);
    }
  }, [sessionQ.isError, sessionQ.error]);

  const healthQ = useQuery({
    queryKey: ["health"],
    queryFn: api.health,
    refetchInterval: 30_000,
  });

  const onSessionCreated = (id: string) => {
    setCurrentSessionId(id);
    setDraftSection(null);
  };

  const localRole = getUserRole();
  const isUnconfirmed = localRole === "unconfirmed";
  const isConfirmed = localRole === "admin" || localRole === "user";

  // Per-page live banner needs page-level event broadcasts (page_started /
  // page_done) which the SQS worker doesn't emit over WebSocket. Currently
  // disabled — the section-level SyncProgressBanner still renders during a
  // sync. If we ever start broadcasting page-level events, recompute this
  // from `activity` and pass it down again.
  const liveJobForSelected: JobActivity | undefined = undefined;

  const tabs: { key: Tab; label: string; icon: typeof BookOpen }[] = [
    { key: "corpus", label: "Corpus", icon: BookOpen },
    { key: "chat", label: "Chat", icon: MessageSquare },
    { key: "history", label: "Sync History", icon: History },
  ];

  const sectionScope = sessionQ.data
    ? {
        sectionId: sessionQ.data.sectionId,
        sectionName: sessionQ.data.sectionName,
        notebookId: sessionQ.data.notebookId,
        notebookName: sessionQ.data.notebookName,
        siteId: sessionQ.data.siteId,
      }
    : draftSection
      ? {
          sectionId: draftSection.sectionId,
          sectionName: draftSection.sectionName,
          notebookId: draftSection.notebookId,
          notebookName: draftSection.notebookName,
          siteId: draftSection.siteId,
        }
      : null;

  if (!healthQ.isLoading && !localRole) {
    return (
      <AuthShell
        onAuthenticated={() => {
          // Re-read health so the new session's role propagates.
          window.location.reload();
        }}
      />
    );
  }

  if (!healthQ.isLoading && isUnconfirmed) {
    return <UnconfirmedPanel />;
  }

  const wsEndpoint = healthQ.data?.wsEndpoint ?? null;
  const idToken = typeof window !== "undefined"
    ? localStorage.getItem("cognito_id_token")
    : null;

  return (
    <WsProvider endpoint={wsEndpoint} token={idToken}>
    <WithActivity>{(activity) => (
    <div className="flex h-screen flex-col p-4">
      {showUserMgmt && <UserManagementModal onClose={() => setShowUserMgmt(false)} />}

      <header className="flex items-center gap-4">
        <div className="shrink-0">
          <h1 className="text-lg font-bold text-slate-900">D&P OneNote Assistant</h1>
        </div>
        {isConfirmed && (
          <div className="flex-1 min-w-0">
            <SearchBar />
          </div>
        )}
        <SignOutButton onAdminClick={() => setShowUserMgmt(true)} />
      </header>

      {healthQ.isLoading && (
        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Checking authentication status...
        </div>
      )}

      {isConfirmed && (
        <>
          <ActivityPanel state={activity} />

          <div className="flex-1 min-h-0">
            <Card className="flex h-full flex-col">
              <div className="flex gap-1 border-b border-slate-200 px-4 pt-3">
                {tabs.map((t) => (
                  <button
                    key={t.key}
                    onClick={() => setActiveTab(t.key)}
                    className={`inline-flex items-center gap-1.5 border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === t.key
                        ? "border-slate-900 text-slate-900"
                        : "border-transparent text-slate-500 hover:border-slate-300 hover:text-slate-700"
                    }`}
                  >
                    <t.icon className="h-4 w-4" />
                    {t.label}
                  </button>
                ))}
              </div>
              <CardContent className="min-h-0 flex-1 pt-4">
                <div className={cn("h-full", activeTab !== "corpus" && "hidden")}>
                  <div className="space-y-6">
                    <CorpusBrowser
                      runningScopeKeys={activity.runningKeys}
                      livePages={{}}
                      rehydratedJobIds={activity.rehydratedJobIds}
                      selectedPageId={selectedPage?.id ?? null}
                      onPageSelected={setSelectedPage}
                    />
                    {selectedPage && (
                      <PageDetailPanel
                        pageId={selectedPage.id}
                        pageContext={{
                          siteId: selectedPage.siteId,
                          notebookId: selectedPage.notebookId,
                          notebookName: selectedPage.notebookName,
                          sectionId: selectedPage.sectionId,
                          sectionName: selectedPage.sectionName,
                          title: selectedPage.title,
                        }}
                        onClose={() => setSelectedPage(null)}
                        liveJob={liveJobForSelected}
                      />
                    )}
                  </div>
                </div>

                <div className={cn("h-full overflow-auto", activeTab !== "history" && "hidden")}>
                  <Suspense fallback={<div className="flex items-center justify-center py-12 text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" />Loading sync history...</div>}>
                    <SyncHistoryView />
                  </Suspense>
                </div>

                <div className={cn("h-full", activeTab !== "chat" && "hidden")}>
                  <div className="flex h-full gap-2">
                    <div className="w-44 shrink-0 overflow-hidden rounded-md border border-slate-200 bg-slate-50/40">
                      <SessionPicker
                        currentSessionId={currentSessionId}
                        onSelect={setCurrentSessionId}
                        draftActive={!currentSessionId}
                      />
                    </div>
                    <div className="min-w-0 flex-1">
                      <ChatPanel
                        sessionId={currentSessionId}
                        onSessionCreated={onSessionCreated}
                        availableProviders={healthQ.data?.providers.available ?? ["openai"]}
                        providerInfo={healthQ.data?.providers.info as any ?? {}}
                        sectionScope={sectionScope}
                        onDraftSectionChange={setDraftSection}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
    )}</WithActivity>
    </WsProvider>
  );
}

/**
 * Render-prop wrapper that calls useSyncActivity inside the WsProvider tree.
 * useSyncActivity subscribes to the WsContext, so it must mount within the
 * provider, not at App's top level.
 */
function WithActivity({
  children,
}: {
  children: (activity: ReturnType<typeof useSyncActivity>) => ReactNode;
}) {
  const activity = useSyncActivity();
  return <>{children(activity)}</>;
}
